import {
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import cx from "classnames";

import { useCombobox } from "./ComboboxContext";

import { Flex } from "../../Flex";
import { Text } from "../../Text";
import { Spinner } from "../../Spinner";

import styles from "../Combobox.module.scss";
import listboxStyles from "../../Listbox/Listbox.module.scss";
import listViewStyles from "../../ListView/ListView.module.scss";
import { ComboboxSelectAll } from "./ComboboxSelectAll";
import { useComboboxGroupContext } from "./ComboboxGroupContext";
import Divider from "../../Divider";

/**
 * Props for the ComboboxGroupList component
 */
export type ComboboxGroupListProps = ComponentPropsWithoutRef<"ul" | "div"> & {
  children?: ReactNode;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ComboboxGroupList = function <Item = any>({
  children,
  ...rest
}: ComboboxGroupListProps) {
  const {
    getMenuProps,
    multiple,
    items,
    hasAddNew = false,
    hasEmpty,
    loading,
    selectAll,
    listRef,
  } = useCombobox<Item>();
  const scrollSentinelRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const { key: groupKey } = useComboboxGroupContext() ?? {};

  const isAGroup = !!groupKey;

  useEffect(() => {
    if (!scrollSentinelRef.current) return;
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            setIsScrolled(true);
          } else {
            setIsScrolled(false);
          }
        });
      },
      { threshold: 0 },
    );
    intersectionObserver.observe(scrollSentinelRef.current);
    return () => {
      intersectionObserver.disconnect();
    };
  }, [scrollSentinelRef, setIsScrolled]);

  if (isAGroup) {
    return <>{children}</>;
  }

  if (multiple) {
    return (
      <>
        <ul
          {...getMenuProps(rest as ComponentPropsWithoutRef<"ul">)}
          ref={listRef as React.MutableRefObject<HTMLUListElement | null>}
          data-anv="combobox-list"
          className={cx(styles["listview"], listViewStyles["listview"], {
            [styles.scrolled]: isScrolled,
          })}
          style={{
            display: items.length || hasAddNew ? undefined : "none",
          }}
        >
          <div ref={scrollSentinelRef} className={styles["scroll-sentinel"]} />
          {!loading && selectAll && (
            <>
              <ComboboxSelectAll />
              <Divider />
            </>
          )}
          {children}
        </ul>

        {loading ? (
          <div className={styles["empty"]}>
            <Flex justifyContent="center">
              <Spinner size="small" />
            </Flex>
          </div>
        ) : !items.length && !hasEmpty ? (
          <div className={styles["empty"]}>
            <Text subdued size="small">
              No match found
            </Text>
          </div>
        ) : null}
      </>
    );
  } else {
    return (
      <>
        <ul
          {...getMenuProps(rest as ComponentPropsWithoutRef<"ul">)}
          ref={listRef as React.MutableRefObject<HTMLUListElement | null>}
          data-anv="combobox-list"
          className={listboxStyles["listbox"]}
          style={{
            display: items.length || hasAddNew ? undefined : "none",
          }}
        >
          {children}
        </ul>

        {loading ? (
          <div className={styles["empty"]}>
            <Flex justifyContent="center">
              <Spinner size="small" />
            </Flex>
          </div>
        ) : !items.length && !hasEmpty ? (
          <div className={styles["empty"]}>
            <Text subdued size="small">
              No match found
            </Text>
          </div>
        ) : null}
      </>
    );
  }
};
